/**
 * RosterEngine.jsx — IMMUTABLE Care Team roster logic (Iter-313).
 *
 * Owns every piece of grouping / display math shared by the two Care Team
 * dispatch surfaces:
 *
 *   1. `MyRoster.jsx`     — the 10-Day Roster (mobile-first day cards).
 *   2. `MyRosterDay.jsx`  — the Daily Worklist (per-day client visit cards).
 *
 * MANDATORY RULE (Iter-313):
 *   One Client ID per Day = ONE Medical Record Button.
 *   Every appointment a guest has on the same calendar date — package
 *   redemptions, linked visits, ad-hoc solo services, any mix thereof —
 *   MUST collapse into a single visit card with ONE medical-record button
 *   labelled by the earliest start time of the day.
 *
 * IMMUTABILITY: Do NOT edit this file during UI, styling, ESLint autofix,
 * Prettier reformat, or any "cleanup" task. Genuine bug fixes require a
 * dedicated task and a matching update to SYSTEM_RULES.md.
 *
 * Purity contract: no React state, no I/O, no DOM. Callers fetch data
 * from `/api/staff/my-roster` and `/api/staff/my-daily-worklist` and pass
 * it in; the engine returns primitives or plain arrays.
 */

// -----------------------------------------------------------------------------
// Date helpers (shared by both roster surfaces)
// -----------------------------------------------------------------------------

export const dayNumber = (iso) => {
  if (!iso) return "";
  const parts = iso.split("-").map(Number);
  return parts[2];
};

export const monthShort = (iso) => {
  if (!iso) return "";
  const [y, m] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, 1);
  return dt.toLocaleString("en-GB", { month: "short" });
};

export const prettyDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
};

// Public-holiday / annual-leave / sick-leave badge palette. Consumed by the
// 10-Day Roster so the same visual language stays consistent across surfaces.
export const LEAVE_LABEL = {
  public_holiday: { text: "PH", full: "Public Holiday", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  annual_leave:   { text: "AL", full: "Annual Leave",   cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  sick_leave:     { text: "SL", full: "Sick Leave",     cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

// -----------------------------------------------------------------------------
// 10-Day Roster helpers
// -----------------------------------------------------------------------------

/**
 * Derive the tap target URL parameters for a roster day. Preserves the
 * previous behaviour (duty branch takes precedence over booking branch;
 * all duty branches are forwarded so multi-branch conflict tabs work).
 *
 * Returns `null` when the day has neither a duty branch nor a booking —
 * caller should treat as "Day Off, nothing scheduled".
 */
export const resolveRosterDayTarget = (day) => {
  const duty = day?.duty_branches || [];
  const bookings = day?.branches || [];
  const primary = duty[0] || bookings[0] || null;
  if (!primary) return null;
  const source = duty.length ? duty : bookings;
  const bidCsv = source.map((b) => b.branch_id).join(",");
  return {
    branch_id: primary.branch_id,
    all_branches: bidCsv,
    date: day.date,
  };
};

/**
 * Derive the compact status of a roster day for the 10-Day tile styling.
 *
 * Returns one of:
 *   missing_report | conflict | on_duty | day_off
 */
export const deriveRosterDayStatus = (day) => {
  if (!day) return "day_off";
  if (day.missing_report) return "missing_report";
  if (day.duty_status) return day.duty_status;
  if (day.conflict) return "conflict";
  if ((day.total_appointments || 0) > 0) return "on_duty";
  return "day_off";
};

/**
 * True when the day should be tappable in the 10-Day roster. Past red
 * compliance days remain tappable so late reports can be filed; yesterday
 * stays tappable even on a day-off; every duty / booking day is tappable.
 */
export const rosterDayTappable = (day) => {
  if (!day) return false;
  if (day.missing_report) return true;
  if (day.is_yesterday) return true;
  if ((day.duty_branches || []).length > 0) return true;
  if ((day.total_appointments || 0) > 0) return true;
  return false;
};

// -----------------------------------------------------------------------------
// Daily Worklist grouping — MANDATORY: One Client ID per Day = One Medical Button
// -----------------------------------------------------------------------------

/**
 * Group a day's appointments by customer_id into client visit cards.
 *
 * Rules:
 *   - Walk-ins with no customer_id fall back to a per-appointment key
 *     so nothing merges by accident under the shared "Guest" label.
 *   - Group Bookings that carry different clients on the same
 *     `group_id` stay as separate cards (one per client) — grouping is
 *     STRICTLY same-customer.
 *   - Services inside each card are sorted earliest-first.
 *   - Cards themselves are ordered by the earliest visit start.
 *
 * Returns an array of client-group objects — each carries the
 * consolidated visit metadata that the Daily Worklist renders.
 */
export const groupAppointmentsByClient = (appointments) => {
  const map = new Map();
  for (const a of (appointments || [])) {
    const key = a.customer_id || `__guest__${a.id}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        customer_id: a.customer_id || null,
        customer_name: a.customer_name || "Guest",
        appts: [],
        has_medical_record: false,
        package_name: "",
        assigned_team: [],
      });
    }
    const g = map.get(key);
    g.appts.push(a);
    g.has_medical_record = g.has_medical_record || !!a.has_medical_record;
    if (!g.package_name && a.package_name) g.package_name = a.package_name;
    if (a.assigned_team && a.assigned_team.length) {
      const merged = new Set([...(g.assigned_team || []), ...a.assigned_team]);
      g.assigned_team = [...merged];
    }
  }
  const groups = [...map.values()];
  for (const g of groups) {
    g.appts.sort((x, y) => (x.start_iso || "").localeCompare(y.start_iso || ""));
  }
  groups.sort((a, b) => (a.appts[0]?.start_iso || "").localeCompare(b.appts[0]?.start_iso || ""));
  return groups;
};

/**
 * Build the SINGLE consolidated medical-record button spec for a client
 * group. Enforces the Iter-313 rule:
 *
 *   One Client ID per Day = ONE Medical Record Button.
 *
 * All appointments in the group (Packages, Linked, Individual, or any
 * mix) collapse into one button. The button carries:
 *
 *   - `key`             — stable key for React `map`.
 *   - `label`           — "Medical Record - {earliest start HH:MM}".
 *   - `representative`  — the earliest appointment (used to open the
 *                          MedicalRecordDialog; the dialog itself
 *                          resolves linked siblings server-side).
 *   - `appts`           — full list of appointments this button covers.
 *   - `linked`          — true when the client has more than one appt
 *                          today (surfaces the link chip on the button).
 *   - `has_record`      — true when EVERY appt in the group already has
 *                          a saved medical record.
 *
 * Returns `null` when the group has no appointments (defensive).
 */
export const buildMedicalRecordButton = (group) => {
  const appts = [...(group?.appts || [])].sort(
    (a, b) => (a.start_iso || "").localeCompare(b.start_iso || ""),
  );
  if (appts.length === 0) return null;
  const representative = appts[0];
  const earliest = representative.start_hm || "Visit";
  return {
    key: `client-${group.customer_id || group.key}`,
    label: `Medical Record - ${earliest}`,
    representative,
    appts,
    linked: appts.length > 1,
    has_record: appts.every((a) => !!a.has_medical_record),
    earliest_hm: representative.start_hm || "",
  };
};

/**
 * True when the client is on their first-ever visit to the brand.
 * Backend stamps `guest_new` per-appointment based on the global
 * medical-record trail; we surface the badge only when EVERY
 * appointment on the client card still reports first-visit status.
 */
export const isNewGuestGroup = (group) => {
  const appts = group?.appts || [];
  if (appts.length === 0) return false;
  return appts.every((a) => a.guest_new === true);
};

/**
 * Derive the strongest visual signal for the client's day and hand it
 * off to `colorForAppt` (imported from the Branch Calendar constants).
 * Priority matches the Branch Calendar: Teal (checked in) > Dark Grey
 * (all completed) > Light Green (deposit paid) > Pink (open).
 *
 * `colorForAppt` is injected so this engine stays framework-pure and
 * doesn't reach into the pages/staff/branch/calendar constants module.
 */
export const deriveClientDayColor = (group, colorForAppt) => {
  const appts = group?.appts || [];
  if (appts.length === 0) return colorForAppt({});
  const teal = appts.find((a) => a.checked_in_at && a.status !== "completed");
  if (teal) return colorForAppt(teal);
  const allCompleted = appts.every((a) => a.status === "completed");
  if (allCompleted) return colorForAppt(appts[0]);
  const dep = appts.find((a) => a.has_deposit || Number(a.deposit_paid ?? a.deposit ?? 0) > 0);
  if (dep) return colorForAppt({ ...dep, status: dep.status === "completed" ? "open" : dep.status });
  return colorForAppt(appts[0]);
};

/**
 * Filter appointments by branch (used by the multi-branch conflict-day
 * tabs on the Daily Worklist). Passing an empty / null branchId returns
 * the list unchanged so a single-branch day renders normally.
 */
export const filterApptsByBranch = (appointments, branchId) => {
  const all = appointments || [];
  if (!branchId) return all;
  return all.filter((a) => a.branch_id === branchId);
};

/**
 * Build a branch-id → branch-name lookup from the raw appointments so
 * multi-branch tabs can render human-friendly labels without an extra
 * network hop.
 */
export const branchNameMap = (appointments) => {
  const map = {};
  for (const a of (appointments || [])) {
    if (a.branch_id && !map[a.branch_id]) map[a.branch_id] = a.branch_name;
  }
  return map;
};

// -----------------------------------------------------------------------------
// Care Team Dispatch — Appointment-date-aware staff resolution
// -----------------------------------------------------------------------------
//
// The Care Team dropdown on the Order · Items surface MUST reflect the Care
// Team that was on the roster on the Appointment Date — NOT today's roster.
// A closed order raised three weeks ago must keep showing the specialists who
// treated the guest even if some of them have since left the clinic.
//
// The two helpers below stay purely functional so they can be unit-tested
// without a React tree and reused by any future consumer surface (POS,
// receipts, reports drill-downs). Global to all 8 branches — the resolution
// is date-driven, never branch-hardcoded.

/**
 * Resolve the "Appointment Date" (Asia/Makassar `YYYY-MM-DD`) of an order.
 *
 * Precedence (highest first):
 *   1. `order.treatment_date`   — operator-stamped treatment date.
 *   2. `order.business_date`    — branch business date the order was raised on.
 *   3. Earliest linked appointment `start_iso` sliced to YYYY-MM-DD.
 *   4. `order.created_at` sliced.
 *
 * Returns `null` when nothing usable exists (e.g. a freshly-staged pending
 * sale with zero items and no dates) so the caller can gracefully fall back
 * to the current-date staff list without a `date=` param.
 */
export const resolveOrderAppointmentDate = (order) => {
  if (!order) return null;
  const isoDay = (v) => (typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : null);
  const direct = isoDay(order.treatment_date) || isoDay(order.business_date);
  if (direct) return direct;
  const items = Array.isArray(order.items) ? order.items : [];
  const starts = items
    .map((it) => isoDay(it?.start_iso) || isoDay(it?.appointment_start_iso))
    .filter(Boolean)
    .sort();
  if (starts.length) return starts[0];
  return isoDay(order.created_at);
};

/**
 * Merge a date-scoped Care Team list with any historical staff already
 * assigned to line items on the order — so a closed / pending-closed
 * order NEVER renders "Unknown" in place of a specialist who has since
 * left the clinic. Order of preference:
 *
 *   1. Every entry from `dateScopedList` (authoritative Care Team as of
 *      the appointment date; already filtered to Care Team preset by the
 *      backend `/branches/{bid}/staff` endpoint).
 *   2. Any `staff_id` referenced by `order.items[*].staff_ids` that is
 *      missing from #1 — looked up first in `fallbackList` (today's
 *      global staff cache) so the human-readable name survives; if still
 *      missing (fully deleted profile), a synthetic
 *      `{ id, full_name: "Former staff" }` placeholder is emitted so the
 *      badge / row never collapses to a blank cell.
 *
 * Pure function: no I/O, no React state. Callers pass in already-fetched
 * data and receive the merged array back.
 */
export const mergeStaffForOrder = ({ dateScopedList, fallbackList, order } = {}) => {
  const primary = Array.isArray(dateScopedList) ? dateScopedList : [];
  const cache = Array.isArray(fallbackList) ? fallbackList : [];
  const seen = new Set(primary.map((s) => s?.id).filter(Boolean));
  const merged = [...primary];
  const items = Array.isArray(order?.items) ? order.items : [];
  for (const it of items) {
    const sids = Array.isArray(it?.staff_ids) ? it.staff_ids : [];
    for (const sid of sids) {
      if (!sid || seen.has(sid)) continue;
      const found = cache.find((s) => s?.id === sid);
      if (found) {
        merged.push(found);
      } else {
        merged.push({ id: sid, full_name: "Former staff", active: false, historical: true });
      }
      seen.add(sid);
    }
  }
  return merged;
};
